import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { useMembers } from "@/lib/members";
import type { Owner } from "@/lib/types";

/** Owner identity colors = chart series slots (Ambos 1, seat 1 → 2, seat 2 → 3). */
export const OWNER_COLOR: Record<Owner, string> = {
  BOTH: "var(--series-1)",
  SEAT_1: "var(--series-2)",
  SEAT_2: "var(--series-3)",
};

type Tone = "neutral" | "accent" | "positive" | "negative" | "warning";

const tones: Record<Tone, string> = {
  neutral: "bg-surface-2 text-muted",
  accent: "bg-accent-soft text-accent",
  positive: "bg-positive/12 text-positive",
  negative: "bg-negative/12 text-negative",
  warning: "bg-warning/14 text-warning",
};

export function Badge({
  tone = "neutral",
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-caption font-medium whitespace-nowrap",
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Owner chip — only rendered when the owner isn't BOTH (household default) and the household
 *  has two members. */
export function OwnerChip({ owner }: { owner: Owner }) {
  const members = useMembers();
  if (owner === "BOTH" || members.single) return null;
  return (
    <Badge>
      <span
        className="size-1.5 rounded-full"
        style={{ background: OWNER_COLOR[owner] }}
        aria-hidden
      />
      <span className="text-text">{members.label(owner)}</span>
    </Badge>
  );
}
